import React, { useEffect, useRef } from 'react';
import { Text, Button } from 'v3/components';
import { Edit2, Trash2 } from 'lucide-react';
import moment from 'moment';
import {
  Container,
  Header,
  ScrollArea,
  Footer,
  PointCard,
  PointHeader,
  PointLetter,
  PointInfo,
  PointTitleLine,
  PointDates,
  PointAddress,
  DeleteBtn
} from './styles';

export default function PointsOfInterest({
  points = [],
  onEdit,
  onDelete,
  onAdd,
  canEditPoint,
  destinationsError = {},
  isSaving = false,
  hasRouteLevelError = false,
  routeErrorMessage = '',
  pointsListRef,
  errorsGro = {},
  routeErrorPointIds = [],
}) {
  const scrollAreaRef = useRef(null);
  const previousPointsLengthRef = useRef(points.length);

  useEffect(() => {
    const previousLength = previousPointsLengthRef.current;
    previousPointsLengthRef.current = points.length;

    if (points.length <= previousLength) return;

    const container = scrollAreaRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [points.length]);

  const getTypeColor = (type) => {
    const id = typeof type === 'string' ? type : type?.id;
    switch (id) {
      case 'collect': return '#28a745';
      case 'customhouse': return '#ffc107';
      case 'delivery': return '#007bff';
      case 'via': return '#f97316';
      default: return '#6c757d';
    }
  };

  const getTypeLabel = (type) => {
    const id = typeof type === 'string' ? type : type?.id;
    switch (id) {
      case 'collect': return 'COLETA';
      case 'customhouse': return 'ADUANA';
      case 'delivery': return 'ENTREGA';
      case 'via': return 'INTERMEDIÁRIO';
      default: return 'PONTO';
    }
  };

  const getAlphabeticPointLabel = (index) => {
    let current = Number(index);
    if (!Number.isInteger(current) || current < 0) return 'A';

    let label = '';
    while (current >= 0) {
      label = String.fromCharCode((current % 26) + 65) + label;
      current = Math.floor(current / 26) - 1;
    }

    return label;
  };

  const getCompletionStyle = (point) => {
    if (point.arrival_time && point.effective_time) {
      return {
        border: '1px solid #b6e4c2',
        color: '#0f5132',
      };
    }

    if (point.arrival_time && !point.effective_time) {
      return {
        border: '1px solid #ffe58f',
        color: '#664d03',
      };
    }

    return {
      border: '1px solid #ced4da',
      color: '#495057',
    };
  };

  const hasOrigin = Boolean(points?.[0]?.lat && points?.[0]?.lng);
  const hasDestination = Array.isArray(points) && points.slice(1).some(d => typeof d?.lat === 'number' && !isNaN(d.lat) && typeof d?.lng === 'number' && !isNaN(d.lng));
  const missingBoth = !hasOrigin && !hasDestination;
  const missingOnlyDestination = hasOrigin && !hasDestination;
  const missingOnlyOrigin = !hasOrigin && hasDestination;
  const showRouteBanner = hasRouteLevelError && (missingBoth || missingOnlyDestination || missingOnlyOrigin);


  const getPointTypeId = (point) => {
    return point?.action?.id || point?.type?.id || point?.type;
  };

  const getDestinationIndex = (point, index) => {
    if (index === 0 || getPointTypeId(point) === 'via') return null;

    return points
      .filter(p => p && p.identifier)
      .slice(1, index)
      .filter(previousPoint => getPointTypeId(previousPoint) !== 'via')
      .length;
  };

  const getDateError = (point, index, field, destinationsError = {}) => {
    if (index === 0) {
      return destinationsError?.[`origin.${field}`];
    }

    const destinationIndex = getDestinationIndex(point, index);
    const indexes = destinationIndex === null
      ? [index]
      : [destinationIndex, index];

    return indexes
      .map(errorIndex => destinationsError?.[`${errorIndex}.${field}`])
      .find(Boolean);
  };

  const hasFieldError = (point, index, field, destinationsError = {}) => {
    return Boolean(getDateError(point, index, field, destinationsError));
  };

  const hasDateError = (point, index, destinationsError = {}) => {
    const start = point.start_schedule?.toString().toLowerCase();
    const end = point.scheduled_time?.toString().toLowerCase();

    const hasFrontendDateError =
      !start || !end || start.includes('inválida') || end.includes('inválida');

    const hasBackendDateError =
      getDateError(point, index, 'start_schedule', destinationsError) ||
      getDateError(point, index, 'scheduled_time', destinationsError);

    return hasFrontendDateError || hasBackendDateError;
  };

  const hasMissingRequiredFields = (point) => {
    if (!point.address || !point.country) return true;

    if (!point.city?.name) {
      return true;
    }

    return false;
  };

  const hasAnyPointError = (point, index, destinationsError = {}) => {
    return (
      hasMissingRequiredFields(point) ||
      hasDateError(point, index, destinationsError)
    );
  };

  const hasRoutePointError = (point, index) => {
    if (!Array.isArray(routeErrorPointIds) || routeErrorPointIds.length === 0) {
      return false;
    }

    const pointKeys = [point?.identifier, point?.id]
      .filter(value => value !== null && value !== undefined)
      .map(value => String(value));

    const hasIdMatch = pointKeys.some(pointKey =>
      routeErrorPointIds.some(errorPointId => String(errorPointId) === pointKey)
    );

    if (hasIdMatch) return true;

    return routeErrorPointIds.some(errorPointId => Number(errorPointId) === index);
  };

  const textGroErrors = (point, index, error) => {

    if (point?.action?.id === 'collect') {
      const hasBackendDateError =
        error?.["origin.start_schedule"] ||
        error?.["origin.scheduled_time"];

      return Boolean(hasBackendDateError);
    }

    if (point?.action?.id === 'delivery') {
      const hasBackendDateError =
        error?.[`destinations[${index - 1}].start_schedule`] ||
        error?.[`destinations[${index - 1}].scheduled_time`];

      return Boolean(hasBackendDateError);
    }

    return false;
  };



  return (
    <Container
      style={{
        border: hasRouteLevelError ? '1px solid #f64e60' : 'none',
        borderRadius: hasRouteLevelError ? '3px' : 'none',
        padding: hasRouteLevelError ? '0.5rem' : '0',
        backgroundColor: hasRouteLevelError ? '#fcf4f4ff' : 'transparent'
      }}
    >
      <Header>
        <Text type="header" style={{ fontSize: '1.1rem', fontWeight: '600' }}>
          Pontos de Interesse
        </Text>

        {showRouteBanner && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              margin: '8px 0 0',
              padding: '2px',
              color: '#f64e60',
              display: 'flex',
              alignItems: 'center',
              fontSize: 13,
              fontWeight: 500,
            }}
            id="route-error-inline"
          >
            <span className="mr-2">⚠️</span>
            {missingBoth && (
              <span>{routeErrorMessage || 'Campos de origens e destinos obrigatórios.*'}</span>
            )}
            {missingOnlyDestination && (
              <span>{routeErrorMessage || 'Inclua pelo menos um destino.*'}</span>
            )}
            {missingOnlyOrigin && (
              <span>{routeErrorMessage || 'Inclua pelo menos uma origem.*'}</span>
            )}
          </div>
        )}
      </Header>

      <ScrollArea ref={scrollAreaRef}>
        <div ref={pointsListRef} />
        {points.length === 0 ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#6c757d', fontSize: '0.9rem' }}>
            Nenhum ponto adicionado ainda.
          </div>
        ) : (
          <>
            {points?.filter(p => p && p.identifier).map((point, index) => (
              <PointCard
                key={point.identifier}
                onClick={() => {
                  if (!canEditPoint(point) || isSaving) return;
                  onEdit(point);
                }}
                style={{
                  ...getCompletionStyle(point),
                  ...(hasAnyPointError(point, index, destinationsError) && {
                    border: '2px solid #dc3545',
                    backgroundColor: '#fff0f0',
                  }),
                  ...(textGroErrors(point, index, errorsGro) && {
                    border: '2px solid #dc3545',
                    backgroundColor: '#fff0f0',
                  }),
                  ...(hasRoutePointError(point, index) && {
                    border: '2px solid #dc3545',
                    backgroundColor: '#fff0f0',
                  }),
                  cursor: canEditPoint(point) && !isSaving ? 'pointer' : 'not-allowed',
                }}
              >
                <PointHeader>
                  <PointLetter style={{ backgroundColor: getTypeColor(point.type) }}>
                    {getAlphabeticPointLabel(index)}
                  </PointLetter>

                  <PointInfo style={{ minWidth: 0 }}>
                    <PointTitleLine
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '0.4rem',
                      }}
                    >
                      <div
                        style={{
                          color: getTypeColor(point.type),
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        #{index + 1} {getTypeLabel(point.type).toUpperCase()}
                      </div>

                      <PointDates
                        title={
                          getDateError(point, index, 'start_schedule', destinationsError) ||
                          getDateError(point, index, 'scheduled_time', destinationsError) ||
                          ''
                        }
                      >
                        <span
                          style={{
                            color: hasFieldError(point, index, 'start_schedule', destinationsError) ? '#dc3545' : '#555',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {point.start_schedule ? (
                            moment(point.start_schedule).format('DD/MM/YYYY HH:mm')
                          ) : (
                            <span style={{ color: '#dc3545' }}>Indefinido</span>
                          )}
                        </span>
                        <span style={{ color: '#555', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>até</span>
                        <span
                          style={{
                            color: hasFieldError(point, index, 'scheduled_time', destinationsError) ? '#dc3545' : '#555',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {point.scheduled_time ? (
                            moment(point.scheduled_time).format('DD/MM/YYYY HH:mm')
                          ) : (
                            <span style={{ color: '#dc3545' }}>Indefinido</span>
                          )}
                        </span>

                        {(hasMissingRequiredFields(point) || hasDateError(point, index, destinationsError)) && (
                          <span
                            title="Este ponto tem campos obrigatórios não preenchidos"
                            style={{ color: '#dc3545', marginLeft: '0.25rem', fontSize: '1rem' }}
                          >
                            ⚠️
                          </span>
                        )}
                      </PointDates>
                    </PointTitleLine>

                    <div style={{ width: '100%', height: '1px', backgroundColor: '#dee2e6', margin: '0.65rem 0 0.5rem' }} />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>

                      <PointAddress style={{ flex: 1, marginRight: 0 }}>
                        {point.formatted_address ||
                          `${point.address || ''}, ${point.number || ''} - ${point.neighborhood || ''}, ${point.city?.name || ''} / ${point.province || ''}`}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                        </div>
                      </PointAddress>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', alignItems: 'center', flexShrink: 0 }}>
                        <DeleteBtn
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isSaving && canEditPoint(point)) onEdit(point);
                          }}
                          title="Editar"
                          disabled={!canEditPoint(point) || isSaving}
                          style={{
                            color: '#13395A',
                            opacity: !canEditPoint(point) || isSaving ? 0.5 : 1,
                            pointerEvents: !canEditPoint(point) || isSaving ? 'none' : 'auto',
                          }}
                        >
                          <Edit2 size={18} />
                        </DeleteBtn>
                        <DeleteBtn
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isSaving && canEditPoint(point)) onDelete(point.identifier);
                          }}
                          title="Remover"
                          disabled={!canEditPoint(point) || isSaving}
                          style={{
                            opacity: !canEditPoint(point) ? 0.5 : 1,
                            pointerEvents: !canEditPoint(point) || isSaving ? 'none' : 'auto',
                          }}
                        >
                          <Trash2 size={18} />
                        </DeleteBtn>
                      </div>
                    </div>
                  </PointInfo>
                </PointHeader>
                <div>
                  {errorsGro && textGroErrors(point, index, errorsGro) && (
                    <Text type="span" color="#dc3545" fontSize="10px">
                      {point?.action?.id === 'collect' && (
                        <>
                          {errorsGro?.["origin.start_schedule"] && (
                            <div>- {errorsGro?.["origin.start_schedule"]}</div>
                          )}
                        </>
                      )}
                    </Text>
                  )}
                </div>
                <div>
                  {errorsGro && textGroErrors(point, index, errorsGro) && (
                    <Text type="span" color="#dc3545" fontSize="10px">
                      {point?.action?.id === 'delivery' && (
                        <>
                          {errorsGro?.[`destinations[${index - 1}].start_schedule`] && (
                            <div>- {errorsGro?.[`destinations[${index - 1}].start_schedule`]}</div>
                          )}
                        </>
                      )}
                    </Text>
                  )}
                </div>
              </PointCard>
            ))}
          </>
        )}
      </ScrollArea>

      <Footer>
        <Button
          variant="primary"
          style={{ width: '100%' }}
          onClick={!isSaving && canEditPoint?.({}) ? onAdd : undefined}
          loading={isSaving}
          disabled={isSaving || !canEditPoint?.({})}
        >
          Incluir destino *
        </Button>
      </Footer>
    </Container>
  );
}
